import { useState } from "react";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { notification } from "antd";

import { SubmitButton } from "@/ui-components/commonComponents/Button/SubmitButton";
import { FeedbackQuestion, FeedbackResponse } from "./candidate-feedback.types";
import { FeedbackScaleQuestion } from "./components/FeedbackScaleQuestion";
import { FeedbackOpenEndedQuestion } from "./components/FeedbackOpenEndedQuestion";
import { CandidateFeedbackHttpService } from "./candidate-feedback-http.service";
import { AssessmentCopy, type AssessmentCopyVariants } from "@/modules/candidate/assessmentCopy";

import styles from "./CandidateFeedbackSurvey.module.css";

const messages = defineMessages({
  thankYou: {
    id: "candidate.feedback.thankYou",
    defaultMessage: "Thank you for your feedback!",
  },
  assessmentSubtitle: {
    id: "candidate.feedback.assessment.subtitle",
    defaultMessage:
      "Your feedback will help inform future improvements to the assessment experience for other candidates.",
  },
  surveySubtitle: {
    id: "candidate.feedback.survey.subtitle",
    defaultMessage: "Your feedback will help inform future improvements to the survey experience for other candidates.",
  },
});

const subtitleMessages: AssessmentCopyVariants = {
  assessment: messages.assessmentSubtitle,
  survey: messages.surveySubtitle,
};

interface CandidateFeedbackSurveyProps {
  questions: FeedbackQuestion[];
  inviteId: number;
  onComplete: () => void;
}

/**
 * Whether every string the candidate needs to answer a question resolved to copy. A question is
 * skipped entirely when any part is missing: an unlabeled field can't be answered meaningfully, and
 * an unlabeled scale option would record a response on a scale the candidate couldn't read — a
 * missing response is safer for analysis than one that looks complete but isn't.
 */
function hasRenderableCopy(question: FeedbackQuestion): boolean {
  return Boolean(question.text.value) && (question.options ?? []).every((option) => Boolean(option.label.value));
}

export function CandidateFeedbackSurvey({ questions, inviteId, onComplete }: CandidateFeedbackSurveyProps) {
  const [responses, setResponses] = useState<Record<number, FeedbackResponse>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const intl = useIntl();

  const sortedQuestions = [...questions].filter(hasRenderableCopy).sort((a, b) => a.displayOrder - b.displayOrder);

  const handleScaleChange = (questionId: number, value: number) => {
    setResponses((prev) => ({
      ...prev,
      [questionId]: { questionId, numericValue: value },
    }));
    setSubmitError(null);
  };

  const handleTextChange = (questionId: number, value: string) => {
    setResponses((prev) => ({
      ...prev,
      [questionId]: { questionId, textValue: value },
    }));
    setSubmitError(null);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitError(null);

    const validResponses = Object.values(responses).filter(
      (r) => r.numericValue !== undefined || (r.textValue && r.textValue.trim()),
    );

    // If no responses, skip submission and complete the survey
    if (validResponses.length === 0) {
      onComplete();
      return;
    }

    setIsSubmitting(true);

    try {
      await CandidateFeedbackHttpService.submitFeedback(inviteId, {
        responses: validResponses,
      });
      notification.success({
        message: intl.formatMessage(messages.thankYou),
      });
      onComplete();
    } catch (error: any) {
      if (error.response?.status === 409) {
        // Already submitted - treat as success
        notification.success({
          message: intl.formatMessage(messages.thankYou),
        });
        onComplete();
      } else {
        setSubmitError(
          intl.formatMessage({
            id: "candidate.feedback.submitError",
            defaultMessage: "Failed to submit feedback. Please try again.",
          }),
        );
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div className={styles.scrollWrapper}>
      <div className={styles.container}>
        <h2 className={styles.title}>
          <FormattedMessage id="candidate.feedback.title" defaultMessage="Share your feedback with us!" />
        </h2>
        <p className={styles.subtitle}>
          <AssessmentCopy variants={subtitleMessages} />
        </p>

        <form onSubmit={handleSubmit}>
          <div className={styles.questionsContainer}>
            {sortedQuestions.map((question) => {
              if (question.type === "scale") {
                return (
                  <FeedbackScaleQuestion
                    key={question.questionId}
                    question={question}
                    value={responses[question.questionId]?.numericValue}
                    onChange={handleScaleChange}
                  />
                );
              }

              if (question.type === "open_ended") {
                return (
                  <FeedbackOpenEndedQuestion
                    key={question.questionId}
                    question={question}
                    value={responses[question.questionId]?.textValue}
                    onChange={handleTextChange}
                  />
                );
              }

              return null;
            })}
          </div>

          {submitError && <p className={styles.errorMessage}>{submitError}</p>}

          <div className={styles.buttonContainer}>
            <SubmitButton isLoading={isSubmitting} disabled={isSubmitting}>
              <FormattedMessage id="candidate.feedback.next" defaultMessage="Next" />
            </SubmitButton>
          </div>
        </form>
      </div>
    </div>
  );
}
